import { db } from "@/firebase/init";
import { doc, getDoc } from "firebase/firestore";

/**
 * Fetch academic calendar for a specific year (defaults to current year)
 * @param {number} [year] - The academic year (e.g., 2025)
 * @returns {Promise<{success: boolean, events: Array, message: string}>}
 */
export const getAcademicCalendar = async (year) => {
  try {
    // Default to current year if not provided
    const targetYear = year || new Date().getFullYear();
    console.log(`[Database Fetch] Getting academic calendar for year: ${targetYear}`);
    
    const calendarRef = doc(db, 'academicCalendar', targetYear.toString());
    const calendarDoc = await getDoc(calendarRef);
    
    if (!calendarDoc.exists()) {
      console.log(`[Database Fetch] Academic calendar not found for year ${targetYear}`);
      return {
        success: false,
        events: [],
        message: `No academic calendar found for ${targetYear}`
      };
    }
    
    const calendarData = calendarDoc.data();
    console.log(`[Database Fetch] Successfully loaded ${calendarData.totalEvents} events`);
    
    return {
      success: true,
      events: calendarData.events || [],
      year: calendarData.year,
      lastUpdated: calendarData.lastUpdated,
      totalEvents: calendarData.totalEvents
    };
  } catch (error) {
    console.error("Error fetching academic calendar:", error);
    return {
      success: false,
      events: [],
      message: error.message
    };
  }
};

/**
 * Get events by category
 * @param {Array} events - Array of calendar events
 * @param {string} category - Category to filter by
 * @returns {Array} - Filtered events
 */
export const getEventsByCategory = (events, category) => {
  return events.filter(event => event.category === category);
};

/**
 * Get upcoming events
 * @param {Array} events - Array of calendar events
 * @param {number} daysAhead - Number of days to look ahead (default: 30)
 * @returns {Array} - Upcoming events sorted by date
 */
export const getUpcomingEvents = (events, daysAhead = 30) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + daysAhead);
  
  return events
    .filter(event => {
      if (event.isRange) {
        const startDate = new Date(event.startDate);
        return startDate >= today && startDate <= futureDate;
      } else {
        const eventDate = new Date(event.date);
        return eventDate >= today && eventDate <= futureDate;
      }
    })
    .sort((a, b) => {
      const dateA = new Date(a.isRange ? a.startDate : a.date);
      const dateB = new Date(b.isRange ? b.startDate : b.date);
      return dateA - dateB;
    });
};

/**
 * Get events for a specific date
 * @param {Array} events - Array of calendar events
 * @param {Date} date - The date to check
 * @returns {Array} - Events on that date
 */
export const getEventsForDate = (events, date) => {
  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);
  
  return events.filter(event => {
    if (event.isRange) {
      const startDate = new Date(event.startDate);
      const endDate = new Date(event.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      return targetDate >= startDate && targetDate <= endDate;
    } else {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate.getTime() === targetDate.getTime();
    }
  });
};

/**
 * Get next event
 * @param {Array} events - Array of calendar events
 * @returns {Object|null} - Next upcoming event or null
 */
export const getNextEvent = (events) => {
  const upcoming = getUpcomingEvents(events, 365);
  return upcoming.length > 0 ? upcoming[0] : null;
};

/**
 * Check if a date is a public holiday
 * @param {Array} events - Array of calendar events
 * @param {Date} date - The date to check
 * @returns {boolean} - True if it's a public holiday
 */
export const isPublicHoliday = (events, date) => {
  const eventsOnDate = getEventsForDate(events, date);
  return eventsOnDate.some(event => event.category === 'public_holiday');
};

/**
 * Format event date for display
 * @param {Object} event - The event object
 * @returns {string} - Formatted date string
 */
export const formatEventDate = (event) => {
  if (event.isRange) {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    return `${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  } else {
    const eventDate = new Date(event.date);
    return eventDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }
};
